/**
 * goalJournal — a durable write-ahead log for ring-goal edits.
 *
 * Mirrors labelJournal.ts (the hardened lap-label journal) so goal saves earn
 * the same durability, minus the per-swim keying: goals are a SINGLETON (there
 * is one goal state, not one per swim), so the journal holds AT MOST ONE entry
 * and the newest edit simply replaces any pending one. Every goal edit is
 * appended here BEFORE it touches the native bridge, and removed only after the
 * write is confirmed (see goalCorrection.ts). An entry that survives a process
 * death is replayed on boot. Backed by localStorage so it survives a force-kill
 * relaunch.
 *
 * Pure + framework-free: no Svelte store, no bridge. The integration layer
 * (goalCorrection.ts) owns the reactive count store and the replay loop.
 */
import type { GoalPreferences } from "@contract/types";

export interface GoalJournalEntry {
  commitId: number;
  prefs: GoalPreferences;
  ts: number;
}

export const GOAL_JOURNAL_KEY = "swimset.goalJournal.v1";

function persist(entry: GoalJournalEntry | null): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (entry) localStorage.setItem(GOAL_JOURNAL_KEY, JSON.stringify(entry));
    else localStorage.removeItem(GOAL_JOURNAL_KEY);
  } catch {
    /* quota / private-mode / purged — degrades to in-session only (known debt) */
  }
}

export function readGoalJournal(): GoalJournalEntry | null {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(GOAL_JOURNAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const e = parsed as GoalJournalEntry;
    if (typeof e.commitId !== "number" || !e.prefs || typeof e.prefs !== "object") return null;
    return e;
  } catch {
    return null;
  }
}

/**
 * Append (record) a goal edit. Newest-wins: goals aren't per-swim like labels,
 * so a new entry REPLACES any pending one — the newest user intent is the only
 * one worth keeping. This mirrors labelJournal's per-swim supersession minus the
 * workoutUUID keying, and — critically — means a stale, still-failed edit can
 * never survive to be replayed over a later successful one.
 */
export function writeGoalJournal(entry: GoalJournalEntry): void {
  persist(entry);
}

/**
 * Remove a confirmed entry, but only when the stored entry is the exact one we
 * confirmed. A newer edit journaled while the older write was still in flight
 * has a higher commitId, so this no-ops and preserves the newer intent —
 * mirroring labelJournal's paired-key removal that stops an in-flight write from
 * wiping a later correction.
 */
export function clearGoalJournal(commitId: number): void {
  const cur = readGoalJournal();
  if (cur && cur.commitId === commitId) persist(null);
}

export function goalJournalPending(): boolean {
  return readGoalJournal() !== null;
}

/** TEST-ONLY: clear the journal between test cases. */
export function _clearGoalJournal(): void {
  persist(null);
}
